import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TurneroLogin from './TurneroLogin';
import TurneroRegister from './TurneroRegister';
import TurneroForgotPassword from './TurneroForgotPassword';
import TurneroResetPassword from './TurneroResetPassword';
import TuFilaLogo from '../resources/TuFilaLogo';
import './TurneroAuth.css';
import { useNavigate, useLocation } from 'react-router-dom';  // 👈 Importa hooks

/**
 * TurneroAuth - Premium Entry point for TuFila Admin Authentication
 * Features Glassmorphism, Framer Motion animations, and Mesh Gradients.
 */
const TurneroAuth = ({ ipserv }) => {

    const navigate = useNavigate();  
    const location = useLocation();
    const [view, setView] = useState('login'); // 'login', 'register', 'forgot', 'reset', or 'success'
    const [resetToken, setResetToken] = useState(null);

    // Auto-redirect if already logged in or detect reset token
    React.useEffect(() => {
        if (localStorage.getItem('tufila_admin')) {
            try {
                const profile = JSON.parse(localStorage.getItem('tufila_admin') || '{}');
                if (profile.AdminRole === 'SUPERADMIN') {
                    navigate('/Turnero/SuperAdmin', { replace: true });
                } else {
                    navigate('/Turnero/Admin', { replace: true });
                }
            } catch {
                navigate('/Turnero/Admin', { replace: true });
            }
            return;
        }

        // Check for reset token in URL
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const isResetPath = location.pathname.includes('reset-password');
        
        if (token && isResetPath) {
            setResetToken(token);
            setView('reset');
        }
    }, [location]);

    const toggleView = () => {
        setView(view === 'login' ? 'register' : 'login');
    };

    const handleRegisterSuccess = () => {
        setView('success');
    };

    return (
        <div className="auth-page-container">
            {/* Background Animated Elements */}
            <div className="auth-blobs">
                <motion.div
                    className="auth-blob"
                    style={{ width: 400, height: 400, background: '#4f46e5', left: '10%', top: '20%' }}
                    animate={{
                        scale: [1, 1.2, 1],
                        x: [0, 50, 0],
                        y: [0, 30, 0]
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="auth-blob"
                    style={{ width: 300, height: 300, background: '#db2777', right: '15%', bottom: '20%' }}
                    animate={{
                        scale: [1, 1.3, 1],
                        x: [0, -40, 0],
                        y: [0, 60, 0]
                    }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                />
            </div>

            {/* Content Split */}
            <div className="auth-side-form">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={view}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                        className="auth-card"
                    >
                        {view === 'login' ? (
                            <TurneroLogin 
                                ipserv={ipserv} 
                                onToggle={toggleView} 
                                onForgot={() => setView('forgot')} 
                            />
                        ) : view === 'register' ? (
                            <TurneroRegister 
                                ipserv={ipserv} 
                                onToggle={toggleView} 
                                onSuccess={handleRegisterSuccess} 
                                onForgot={() => setView('forgot')}
                            />
                        ) : view === 'forgot' ? (
                            <TurneroForgotPassword 
                                onBack={() => setView('login')} 
                                onSuccess={() => setView('login')} 
                            />
                        ) : view === 'reset' ? (
                            <TurneroResetPassword 
                                token={resetToken} 
                                onSuccess={() => {
                                    setResetToken(null);
                                    setView('login');
                                }} 
                            />
                        ) : (
                            <div className="text-center p-4">
                                <div className="success-animation-icon mb-4 mx-auto">
                                    <div className="pulse-success"></div>
                                    <TuFilaLogo size={80} />
                                </div>
                                <h2 className="auth-title mb-3">¡Registro Exitoso!</h2>
                                <p className="text-white mb-4 px-3" style={{ fontSize: '1.1rem', lineHeight: '1.6', opacity: 0.9 }}>
                                    Hemos enviado un enlace de verificación a tu correo electrónico.<br/>
                                    <strong>Por favor, confirma tu cuenta para poder ingresar.</strong>
                                </p>
                                <div className="p-3 bg-glass-dark border-radius-12 mb-5">
                                    <small className="text-white" style={{ opacity: 0.7 }}>¿No recibiste el correo? Revisa tu carpeta de spam o espera unos minutos.</small>
                                </div>
                                <button 
                                    className="btn-premium-solid w-100 py-3"
                                    onClick={() => setView('login')}
                                >
                                    VOLVER AL INICIO DE SESIÓN
                                </button>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="auth-side-visual">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="text-center"
                >
                    <TuFilaLogo size={500} className="mb-4" />
                    <h2 className="auth-title" style={{ fontSize: '3.5rem' }}>TuFila Admin</h2>
                    <p className="auth-subtitle" style={{ fontSize: '1.25rem', opacity: 0.7 }}>
                        La nueva era en gestión de filas.<br />
                        Simplicidad. Poder. Elegancia.
                    </p>
                </motion.div>

                {/* Decorative Elements */}
                <div className="position-absolute bottom-0 start-0 p-5 opacity-25">
                    <small>© 2026 OctoCore Technologies</small>
                </div>
            </div>
        </div>
    );
};

export default TurneroAuth;
